/**
 * The Quarantine-by-reason section's data source (REQ-STATUS-100).
 *
 * The snapshot's `quarantined_total` says *that* messages were refused; this
 * says *what* refused them — the half REQ-STATUS-097 claimed was on the display
 * and was not (it existed only as a Grafana-side Prometheus label).
 *
 * Fetched lazily, like the Trend section's history: the caller passes the
 * section's expanded state, so a collapsed section issues no request.
 *
 * # The two numbers do not agree, and the UI must not pretend they do
 *
 * `quarantined_total` counts what the *process* has done since it started;
 * `total` here counts what the drain *persisted* inside the window. Neither is
 * derivable from the other, so the section labels its own scope rather than
 * presenting a figure that looks like it should reconcile with the metric row a
 * few lines above it.
 *
 * Cross-references:
 *   - REQ-STATUS-100 (this read), REQ-STATUS-097 (the obligation it closes).
 *   - REQ-STORAGE-061 (the table), REQ-QUERY-058 (the type the rows link to).
 *   - `use-component-history` — the sibling lazy read this mirrors in shape.
 */
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import { useSession } from "@/auth/session-context";
import { SYSTEM_STATUS_READER_ROLES } from "@/system-status/use-system-status-snapshot";
import { type HistoryWindow, HISTORY_MIN_REFRESH_MS } from "@/system-status/use-component-history";
import { useLiveFrameRefresh } from "@/system-status/use-live-frame-refresh";
import type { components } from "@/lib/api-types.gen";

/** The server's breakdown payload, straight from the generated schema. */
export type QuarantineBreakdown = components["schemas"]["QuarantineBreakdownModel"];
/** One reason row. */
export type QuarantineReason = components["schemas"]["QuarantineReasonModel"];

/**
 * The queryable type id the rows link to (REQ-QUERY-058).
 *
 * Named once here rather than inline at the click handler so the handoff and
 * any future second link cannot disagree about it.
 */
export const QUARANTINE_TYPE_ID = "quarantine";

/**
 * Why a breakdown carries no counts, when it carries none for a reason.
 *
 * ⚠ These are **not** interchangeable with an empty `reasons` list, and the
 * distinction is the whole point: an empty list with `unavailable === null` is a
 * measured zero — real, reassuring, worth showing. A non-null value means the
 * zero is not a measurement, and rendering "0" for it would turn a database
 * blip into a clean bill of health (REQ-STATUS-016; CLAUDE.md §8.1).
 */
export const QUARANTINE_UNAVAILABLE = {
  /** The component has no quarantine concept — render nothing at all. */
  notAnIngestComponent: "not_an_ingest_component",
  /** The read failed — say so; never show a count. */
  readFailed: "read_failed",
} as const;

export const quarantineReasonsKey = (
  componentId: string,
  window: HistoryWindow,
): readonly unknown[] => ["system-status", "quarantine-reasons", componentId, window] as const;

/**
 * Fetch one component's quarantine breakdown.
 *
 * `enabled` gates the request on the section being open, mirroring
 * REQ-STATUS-072's bounded-cost rule for the Trend section.
 */
export function useQuarantineReasons({
  componentId,
  window,
  enabled,
}: {
  componentId: string | null;
  window: HistoryWindow;
  enabled: boolean;
}): UseQueryResult<QuarantineBreakdown, ApiError> {
  const session = useSession();
  const gated = enabled && componentId !== null && session.hasAnyRole(SYSTEM_STATUS_READER_ROLES);
  const query = useQuery<QuarantineBreakdown, ApiError>({
    queryKey: quarantineReasonsKey(componentId ?? "", window),
    // The same four-role gate as the snapshot: an operator is the role that
    // notices a platform going quiet, so the *why* cannot be held behind a
    // role the person asking does not hold (ADR-036 §D-7 / REQ-PEER-042).
    enabled: gated,
    queryFn: async ({ signal }) => {
      const path = `/api/system/components/${encodeURIComponent(
        componentId ?? "",
      )}/quarantine-reasons?window=${window}`;
      return await apiFetch<QuarantineBreakdown>(path, { signal });
    },
    // A grouped count over a bounded window — the same reasoning as the history
    // read, and the same floor: quarantines arrive by drain, so a faster re-read
    // redraws identical counts.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // The BFF's RBAC verdict is authoritative; retrying a 403 repeats a denial.
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false;
      return failureCount < 2;
    },
  });
  // A new live frame is the signal that the collector — and therefore the drain
  // behind it — has moved (REQ-STATUS-099).
  useLiveFrameRefresh({
    queryKey: quarantineReasonsKey(componentId ?? "", window),
    refetch: query.refetch,
    minIntervalMs: HISTORY_MIN_REFRESH_MS,
    enabled: gated,
  });

  return query;
}
